/**
 * 外殼：@tpte/ui 的 `AppShell`（桌面上 246px 墨藍側欄＋52.5px 頂列；手機底部 4 格 tab）。
 *
 * 這一層**只做外框與導航**，不碰任何收件匣行為。
 *
 * ## 四區（T01 座標系）
 *
 * 側欄分成**四組**，一組一區；每組底下的 item 是「這一區現有的視圖」（二級）。
 *
 * | 一級（Zone） | 二級（Section） | 側欄 item | 網址 |
 * |:--|:--|:--|:--|
 * | 工作台 | `workspace` | 收件匣 | `#/inbox` |
 * | 身分 | `identities` | 身分總表 | `#/identities` |
 * | 房間 | `rooms`／`sg` | 房間列表／sugargirl 女孩 | `#/rooms`／`#/sg` |
 * | 系統 | `system` | 總覽／工作階段／監控／參數／授權／邀請／AI | `#/system/<tab>` |
 *
 * - 手機（<768px）：底部**只有四格＝四區**。有第二層的區（房間／系統）點下去開該區的
 *   二級清單（BottomNav 的 hub 面板），所以「一級＋當前區二級」兩層都在，不會掉功能。
 * - 桌機：群組標題就是區名，hub 列可展開二級；單一視圖的區（工作台／身分）直接導頁。
 * - `useInbox` 不在這裡（在 App 最外層），所以切換 section 不會重新掛載任何訂閱。
 * - 舊書籤（`#/health`、`#/admin/sessions`、`#/accounts`…）在 `routes.ts` 解析後就被
 *   正規化成新網址，這裡不需要第二套 alias 判斷。
 */
import type { ReactNode } from 'react';
import { AppShell, type NavConfig } from '@tpte/ui';
import type { UseDensityResult } from '@chat-rea/console-ui';
import { TopbarActions, type PoolHealth, type TopbarActionsProps } from './blocks/TopbarActions';
import type { SystemTab } from './routes';

/**
 * 一級四區。**區是導覽的單位，不是頁面的單位**：一區可以有多個視圖（二級）。
 */
export type Zone = 'workspace' | 'identities' | 'rooms' | 'system';

/** 四區的固定順序（側欄分組、手機底部四格、驗收都吃這一份）。 */
export const ZONES: readonly Zone[] = ['workspace', 'identities', 'rooms', 'system'];

/** 視圖（二級）。`sg` 掛在房間底下，網址自成一個（`#/sg` 深連結保留）。 */
export type Section = 'workspace' | 'waterfall' | 'identities' | 'rooms' | 'sg' | 'system';

/** 區名：側欄群組標題、頂列 zone、以及手機底部四格。 */
export const ZONE_LABEL: Record<Zone, string> = {
  workspace: '工作台',
  identities: '身分',
  rooms: '房間',
  system: '系統',
};

/** 視圖 → 所屬區。只有這一處決定歸屬，別頁要引用就寫「見 系統」。 */
export const SECTION_ZONE: Record<Section, Zone> = {
  workspace: 'workspace',
  waterfall: 'workspace',
  identities: 'identities',
  rooms: 'rooms',
  sg: 'rooms',
  system: 'system',
};

/** 視圖名稱：頂列標題與 403 畫面共用同一份。 */
export const SECTION_LABEL: Record<Section, string> = {
  workspace: '收件匣',
  waterfall: '訊息瀑布',
  identities: '身分',
  rooms: '房間列表',
  sg: 'SG 女孩',
  system: '系統',
};

/** 系統區的第二層（`systemTab`）；標籤與 `#/system/<tab>` 一一對應。 */
export const SYSTEM_NAV: readonly { tab: SystemTab; label: string }[] = [
  { tab: 'overview', label: '總覽' },
  { tab: 'sessions', label: '工作階段' },
  { tab: 'monitor', label: '監控績效' },
  { tab: 'rooms-map', label: '房間地圖' },
  { tab: 'params', label: '運行參數' },
  { tab: 'grants', label: '聊手權限' },
  { tab: 'invites', label: '帳號邀請' },
  { tab: 'ai', label: 'AI 成效' },
];

export interface ConsoleShellProps {
  section: Section;
  /** 系統區目前的分頁（二級）。`section !== 'system'` 時不影響任何畫面。 */
  systemTab?: SystemTab;
  onSectionChange: (section: Section) => void;
  onSystemTabChange?: (tab: SystemTab) => void;
  density: UseDensityResult;
  topbar: Omit<TopbarActionsProps, 'density' | 'poolHealth'>;
  /**
   * 帳號池健康度（可用／總數）。直接在 `App.tsx` 從 `c.poolAccounts` 算好傳進來，
   * 不塞進 `topbar`：那是「跟這一頁內容有關的數字」（未讀／對話數），池子是跨頁的營運事實。
   * 沒傳＝頂列不出現這一項（其餘三項照舊）。
   */
  poolHealth?: PoolHealth;
  /** 側欄底部：目前身分（沒有就顯示「未選身分」）。 */
  identity?: { name: string; role?: string };
  /**
   * 是否為系統管理員。role 來自 auth-service，不是前端名單。
   * 沒傳＝false。側欄只剩工作台與身分，房間與聊手權限／帳號邀請不會出現。
   */
  canAdmin?: boolean;
  /** 系統工程師：可看運行參數／AI／監控（不含聊手授權）。 */
  canEngineer?: boolean;
  children: ReactNode;
}

export function ConsoleShell({
  section,
  systemTab = 'overview',
  onSectionChange,
  onSystemTabChange,
  density,
  topbar,
  poolHealth,
  identity,
  canAdmin = false,
  canEngineer = false,
  children,
}: ConsoleShellProps) {
  const staff = canAdmin || canEngineer;
  const zone = SECTION_ZONE[section];

  /**
   * 選單分兩套（#08）：身分不再只是「唯讀 vs 可寫」，而是**看得到哪些區**。
   *
   * - 管理員：四區（工作台／身分／房間／系統），每區底下放現有視圖。
   * - 聊手：只有工作台與身分兩區。房間、系統整組不出現——不是 disable，是根本不存在。
   *
   * 同一份名單也決定網址能不能進（`canAccessSection()`，見 `shell/routes.ts`），
   * 所以「選單藏起來」與「貼網址」不會一邊鬆一邊緊。
   */
  const systemTabs = SYSTEM_NAV.filter((entry) => {
    if (entry.tab === 'grants' || entry.tab === 'invites') return canAdmin;
    if (entry.tab === 'params' || entry.tab === 'monitor' || entry.tab === 'ai') {
      return staff;
    }
    return staff;
  });

  // 工作台維持「一區一格」：收件匣／瀑布收在 children，手機底欄才不會變成五格把「系統」擠掉。
  const workspaceItems = [
    {
      id: 'workspace',
      label: ZONE_LABEL.workspace,
      shortLabel: ZONE_LABEL.workspace,
      href: '#/inbox',
      active: section === 'workspace' || section === 'waterfall',
      badge: topbar.unreadTotal > 0 ? topbar.unreadTotal : undefined,
      onSelect: () => onSectionChange('workspace'),
      children: [
        {
          id: 'inbox',
          label: SECTION_LABEL.workspace,
          href: '#/inbox',
          active: section === 'workspace',
          badge: topbar.unreadTotal > 0 ? topbar.unreadTotal : undefined,
          onSelect: () => onSectionChange('workspace'),
        },
        {
          id: 'waterfall',
          label: SECTION_LABEL.waterfall,
          href: '#/waterfall',
          active: section === 'waterfall',
          onSelect: () => onSectionChange('waterfall'),
        },
      ],
    },
  ];

  const nav: NavConfig = [
    {
      id: 'workspace',
      label: ZONE_LABEL.workspace,
      items: workspaceItems,
    },
    {
      id: 'identities',
      label: ZONE_LABEL.identities,
      items: [
        {
          id: 'identities',
          label: '身分總表',
          shortLabel: ZONE_LABEL.identities,
          href: '#/identities',
          active: section === 'identities',
          onSelect: () => onSectionChange('identities'),
        },
      ],
    },
    ...(canAdmin
      ? [
          {
            id: 'rooms',
            label: ZONE_LABEL.rooms,
            items: [
              {
                id: 'rooms',
                label: SECTION_LABEL.rooms,
                shortLabel: ZONE_LABEL.rooms,
                href: '#/rooms',
                active: section === 'rooms' || section === 'sg',
                onSelect: () => onSectionChange('rooms'),
                children: [
                  {
                    id: 'rooms-list',
                    label: SECTION_LABEL.rooms,
                    href: '#/rooms',
                    active: section === 'rooms',
                    onSelect: () => onSectionChange('rooms'),
                  },
                  {
                    id: 'sg',
                    label: SECTION_LABEL.sg,
                    href: '#/sg',
                    active: section === 'sg',
                    onSelect: () => onSectionChange('sg'),
                  },
                ],
              },
            ],
          },
        ]
      : []),
    ...(staff
      ? [
          {
            id: 'system',
            label: ZONE_LABEL.system,
            items: [
              {
                id: 'system',
                label: '系統',
                shortLabel: ZONE_LABEL.system,
                href: '#/system/overview',
                active: section === 'system',
                onSelect: () => onSectionChange('system'),
                children: systemTabs.map((entry) => ({
                  id: `system-${entry.tab}`,
                  label: entry.label,
                  href: `#/system/${entry.tab}`,
                  active: section === 'system' && systemTab === entry.tab,
                  onSelect: () => onSystemTabChange?.(entry.tab),
                })),
              },
            ],
          },
        ]
      : []),
  ];

  return (
    <div
      className={`h-dvh w-full ${density.className}`}
      data-testid="console-shell"
      data-density={density.density}
      data-zone={zone}
    >
      <AppShell
        className="h-full"
        nav={nav}
        brand={
          <div className="flex min-w-0 flex-col leading-none">
            <span className="ak-display truncate text-[17px] text-sidebar-foreground">chat-rea</span>
            <span className="mt-0.5 font-mono text-[9px] uppercase tracking-micro text-sidebar-muted">
              console
            </span>
          </div>
        }
        sidebarUser={
          identity
            ? { name: identity.name, role: identity.role }
            : { name: '未選身分', role: '選一個對話' }
        }
        title={SECTION_LABEL[section]}
        eyebrow="CHAT-REA"
        topbarActions={
          <TopbarActions
            {...topbar}
            poolHealth={poolHealth}
            density={density}
            identity={identity}
            zone={ZONE_LABEL[zone]}
          />
        }
        mobileSlots={4}
        gridAlpha={0.6}
      >
        {children}
      </AppShell>
    </div>
  );
}
